/**
 * Phone number utility functions
 * Uses libphonenumber-js for enterprise-grade phone number parsing
 */

import { parsePhoneNumber, type CountryCode } from "libphonenumber-js";

/**
 * Normalizes various phone number formats to E.164 format
 * Handles all French formats: 0672250986, 672250986, 330672250986, 33672250986,
 * 00330672250986, +330672250986, +33672250986
 *
 * Uses libphonenumber-js (based on Google's libphonenumber) for robust parsing
 */
export const normalizePhoneNumber = (
  phone: string,
  _countryCode: string = "+33", // Default to +33 for French numbers
): string => {
  if (!phone || !phone.trim()) {
    return phone;
  }

  // Remove all spaces, dashes, dots, parentheses for parsing
  let cleaned = phone.replace(/[\s\-.()]/g, "");

  // Extract country code (e.g., "FR" from "+33")
  const country: CountryCode = "FR"; // Always use FR for French numbers

  try {
    // Handle 00 prefix (international format without +)
    if (cleaned.startsWith("00")) {
      cleaned = "+" + cleaned.substring(2);
    }

    // If it already has +, try parsing as-is first
    if (cleaned.startsWith("+")) {
      try {
        const phoneNumber = parsePhoneNumber(cleaned);
        return phoneNumber.format("E.164");
      } catch {
        // If parsing fails, continue to country-specific parsing
      }
    }

    // Try parsing with country context (handles local formats)
    const phoneNumber = parsePhoneNumber(cleaned, country);

    // Return in E.164 format (e.g., +33672250986)
    return phoneNumber.format("E.164");
  } catch (error) {
    // Fallback: handle common French formats manually
    // This should rarely be needed, but provides safety net

    // Handle 00 prefix
    if (cleaned.startsWith("00")) {
      cleaned = "+" + cleaned.substring(2);
    }

    // If it already has +, return as is (let Twilio validate)
    if (cleaned.startsWith("+")) {
      return cleaned;
    }

    // Handle French local format: 0672250986 → +33672250986
    if (cleaned.startsWith("0") && cleaned.length === 10) {
      return `+33${cleaned.substring(1)}`;
    }

    // Handle 9-digit format: 672250986 → +33672250986
    if (/^\d{9}$/.test(cleaned)) {
      return `+33${cleaned}`;
    }

    // Handle format with country code: 330672250986 or 33672250986 → +33672250986
    if (cleaned.startsWith("33") && cleaned.length >= 11) {
      // Check if it's 330672250986 (with leading 0) or 33672250986 (without)
      if (cleaned.length === 12 && cleaned.startsWith("330")) {
        // Format: 330672250986 → +33672250986 (remove extra 0)
        return `+33${cleaned.substring(3)}`;
      } else if (cleaned.length === 11 && cleaned.startsWith("33")) {
        // Format: 33672250986 → +33672250986
        return `+${cleaned}`;
      }
    }

    // Last resort: return cleaned number
    // This will be validated by Twilio downstream
    console.warn(
      `Failed to normalize phone number: ${phone}. Using fallback.`,
      error,
    );
    return cleaned.startsWith("+")
      ? cleaned
      : `+33${cleaned.replace(/^0/, "")}`;
  }
};
/**
 * Formats a phone number in E.164 format to a human-readable format
 * Example: +33612345678 -> +33  6  12  34  56  78
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  // Remove all spaces, dashes, dots, parentheses
  const cleaned = phone.replace(/[\s\-.()]/g, "");

  // If it starts with +, format it nicely
  if (cleaned.startsWith("+")) {
    // Extract country code (usually 1-3 digits after +)
    const match = cleaned.match(/^\+(\d{1,3})(\d+)$/);
    if (match) {
      const [, countryCode, number] = match;

      // For French numbers (+33), format as +33  X  XX  XX  XX  XX (with extra spaces)
      if (countryCode === "33" && number.length === 9) {
        return `+33  ${number[0]}  ${number.substring(1, 3)}  ${number.substring(3, 5)}  ${number.substring(5, 7)}  ${number.substring(7, 9)}`;
      }

      // For other formats, add double spaces every 2 digits after country code
      const formattedNumber = number.match(/.{1,2}/g)?.join("  ") || number;
      return `+${countryCode}  ${formattedNumber}`;
    }
  }

  // If no +, try to format as local number with extra spaces
  if (cleaned.length === 10 && cleaned.startsWith("0")) {
    return `${cleaned[0]}${cleaned[1]}  ${cleaned.substring(2, 4)}  ${cleaned.substring(4, 6)}  ${cleaned.substring(6, 8)}  ${cleaned.substring(8, 10)}`;
  }

  // Fallback: return as is
  return phone;
};
